import { Head, Link, router } from "@inertiajs/react"
import { Check, Minus, Plus } from "lucide-react"
import { useState } from "react"

import { Heading } from "@/components/heading"
import { HuePicker } from "@/components/hue-picker"
import { InputError } from "@/components/input-error"
import { RaceTag } from "@/components/race-tag"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { formatFullGold, formatGold } from "@/lib/format"
import { getTeamInitials } from "@/lib/race-colors"
import { cn } from "@/lib/utils"
import { leaguePath, leagueTeamsPath, newLeagueTeamPath } from "@/routes"
import type { BreadcrumbItem, RosterDraftingProps } from "@/types"

import { FormErrorSummary } from "./form-error-summary"
import { PositionalShop } from "./positional-shop"
import { StaffExtrasCard } from "./staff-extras-card"
import { BUDGET, positionCount, useRosterReducer } from "./use-roster-reducer"

interface StepperProps {
  label: string
  value: number
  cost: number
  onDecrement: () => void
  onIncrement: () => void
  min?: number
}

function Stepper({
  label,
  value,
  cost,
  onDecrement,
  onIncrement,
  min = 0,
}: StepperProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-[var(--accent-dim)]">
          {formatGold(cost)} GP each
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={onDecrement}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >
          <Minus className="size-3" />
        </Button>
        <span className="w-5 text-center text-sm font-bold">{value}</span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={onIncrement}
          aria-label={`Increase ${label}`}
        >
          <Plus className="size-3" />
        </Button>
      </div>
    </div>
  )
}

export default function RosterDrafting({
  league,
  race,
  errors,
}: RosterDraftingProps) {
  const { state, dispatch, derived } = useRosterReducer(race)
  const [colorHue, setColorHue] = useState<number | null>(null)

  const teamInitials = getTeamInitials(state.teamName)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
    { title: "Build Team", href: newLeagueTeamPath(league.id) },
    { title: race.name },
  ]

  const budgetPct = Math.min((derived.totalCost / BUDGET) * 100, 100)
  const budgetBarClass = cn(
    "h-full transition-[width,background] duration-300",
    derived.overBudget
      ? "bg-gradient-to-b from-[oklch(0.55_0.24_28)] to-[oklch(0.4_0.2_28)] shadow-[inset_0_1px_0_oklch(0.65_0.22_28/0.3),0_0_8px_oklch(0.55_0.24_28/0.3)]"
      : derived.hasEnoughPlayers
        ? "bg-gradient-to-b from-[oklch(0.45_0.12_145)] to-[oklch(0.35_0.1_145)] shadow-[inset_0_1px_0_oklch(0.55_0.14_145/0.3)]"
        : "bg-gradient-to-b from-[oklch(0.52_0.23_28)] to-[oklch(0.4_0.18_28)] shadow-[inset_0_1px_0_oklch(0.6_0.2_28/0.3)]",
  )

  function handleConfirm() {
    if (!derived.isValid) return
    router.post(leagueTeamsPath(league.id), {
      team: {
        name: state.teamName,
        race: race.key,
        color_hue: colorHue,
        rerolls: state.rerolls,
        players: state.players.map((p) => ({
          position: p.position,
          name: p.name,
        })),
      },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Draft Roster · ${race.name}`} />

      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        {/* Team identity card */}
        <div className="mb-10">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <RaceTag race={race.key} />
                <Link
                  href={newLeagueTeamPath(league.id)}
                  className="text-muted-foreground text-xs transition-colors hover:text-[var(--accent)]"
                >
                  ← Change race
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid items-start gap-6 sm:grid-cols-2">
                <div>
                  <Label
                    htmlFor="team-name"
                    className="mb-2 block text-xs font-bold tracking-widest uppercase"
                    style={{ color: "var(--accent-dim)" }}
                  >
                    Team Name
                  </Label>
                  <Input
                    id="team-name"
                    placeholder={`e.g. The ${race.name} Bashers`}
                    value={state.teamName}
                    onChange={(e) =>
                      dispatch({ type: "SET_TEAM_NAME", name: e.target.value })
                    }
                    aria-label="Team name"
                    aria-invalid={!!errors?.name || undefined}
                    aria-describedby={
                      errors?.name ? "team-name-error" : undefined
                    }
                    className="font-display h-14 text-2xl font-bold"
                  />
                  <InputError id="team-name-error" messages={errors?.name} />
                </div>
                <div>
                  <Label
                    htmlFor="team-color"
                    className="mb-2 block text-xs font-bold tracking-widest uppercase"
                    style={{ color: "var(--accent-dim)" }}
                  >
                    Team Color
                  </Label>
                  <HuePicker
                    id="team-color"
                    race={race.key}
                    shieldLabel={teamInitials}
                    value={colorHue}
                    onChange={setColorHue}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <FormErrorSummary
          errors={errors}
          title="Please fix the following errors:"
        />

        <div className="grid gap-10 lg:grid-cols-5">
          {/* Left: Positional Shop (3 cols) */}
          <div className="lg:col-span-3">
            <PositionalShop
              positions={race.positions}
              count={(position) => positionCount(state, position.name)}
              hireDisabled={(position) =>
                positionCount(state, position.name) >= position.quantity ||
                state.players.length >= 16
              }
              onHire={(position) => dispatch({ type: "HIRE_PLAYER", position })}
            />
          </div>

          {/* Right: Roster panels (2 cols, sticky) */}
          <div className="lg:col-span-2">
            <div className="space-y-10 lg:sticky lg:top-4 lg:self-start">
              {/* Budget Bar */}
              <Card className="py-0">
                <CardContent className="pt-5 pb-4">
                  <div className="mb-2 flex items-baseline justify-between">
                    <Heading
                      level={3}
                      wrapperClassName="mb-0"
                      className="font-bold"
                    >
                      Treasury
                    </Heading>
                    <div className="text-right">
                      <span className="text-lg font-bold">
                        {formatFullGold(derived.totalCost)}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        {" "}
                        / 1,000,000 gp
                      </span>
                    </div>
                  </div>
                  <div className="border-border bg-input relative h-5 overflow-hidden border">
                    <div
                      className={budgetBarClass}
                      style={{ width: `${budgetPct}%` }}
                    />
                  </div>
                  <div
                    className="mt-1.5 flex items-center justify-between text-xs"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    <span>
                      <span
                        className="font-semibold"
                        style={{ color: "var(--foreground)" }}
                      >
                        {formatFullGold(Math.max(0, derived.remainingBudget))}
                      </span>{" "}
                      gp remaining
                    </span>
                    <span>{state.players.length} / 11 minimum</span>
                  </div>
                  {derived.overBudget && (
                    <p className="text-destructive mt-1 text-xs font-semibold">
                      Over budget — remove players or staff to continue
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Staff & Extras */}
              <StaffExtrasCard>
                <Stepper
                  label="Rerolls"
                  value={state.rerolls}
                  cost={race.reroll_cost}
                  onDecrement={() =>
                    dispatch({
                      type: "SET_REROLLS",
                      count: state.rerolls - 1,
                    })
                  }
                  onIncrement={() =>
                    dispatch({
                      type: "SET_REROLLS",
                      count: state.rerolls + 1,
                    })
                  }
                />
              </StaffExtrasCard>

              {/* Your Roster */}
              <Card className="py-0">
                <CardContent className="pt-5 pb-4">
                  <Heading level={2} wrapperClassName="mb-3">
                    Your Roster
                  </Heading>

                  {state.players.length === 0 ? (
                    <p
                      className="py-4 text-center text-sm"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      Hire players from the left panel
                    </p>
                  ) : (
                    <div
                      className="border-t"
                      style={{ borderColor: "var(--border)" }}
                    >
                      {state.players.map((player, index) => (
                        <div
                          key={player.id}
                          className="flex items-center gap-2 border-b px-2 py-2 transition-[background] duration-100 hover:bg-[oklch(0.1_0.005_230)]"
                          style={{ borderColor: "var(--border)" }}
                        >
                          <span
                            className="w-5 shrink-0 text-center text-xs font-bold"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            #{index + 1}
                          </span>
                          <Input
                            value={player.name}
                            onChange={(e) =>
                              dispatch({
                                type: "RENAME_PLAYER",
                                id: player.id,
                                name: e.target.value,
                              })
                            }
                            className="h-8 min-w-0 flex-1 text-xs"
                            aria-label={`Player name: ${player.name}`}
                          />
                          <span
                            className="shrink-0 text-xs"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            {player.position}
                          </span>
                          <Button
                            type="button"
                            variant="fire"
                            className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                            onClick={() =>
                              dispatch({
                                type: "FIRE_PLAYER",
                                id: player.id,
                              })
                            }
                            aria-label={`Fire ${player.name}`}
                          >
                            Fire
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Roster footer */}
                  <div
                    className="mt-4 flex items-center justify-between border-t pt-3"
                    style={{ borderColor: "var(--border-heavy)" }}
                  >
                    <div>
                      <span
                        className="text-xs font-semibold tracking-wide uppercase"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        Total value
                      </span>
                      <p className="text-sm font-bold">
                        {formatFullGold(derived.playerCost)} gp
                      </p>
                    </div>
                    <div className="text-right">
                      {state.players.length < 11 ? (
                        <span
                          className="px-2 py-0.5 text-xs font-bold"
                          style={{
                            background: "oklch(0.25 0.08 28)",
                            color: "oklch(0.70 0.18 28)",
                            border: "1px solid oklch(0.38 0.12 28)",
                          }}
                        >
                          need {11 - state.players.length} more
                        </span>
                      ) : (
                        <span
                          className="px-2 py-0.5 text-xs font-bold"
                          style={{
                            background: "oklch(0.20 0.08 145)",
                            color: "oklch(0.65 0.18 145)",
                            border: "1px solid oklch(0.35 0.12 145)",
                          }}
                        >
                          {state.players.length} players
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Confirm button */}
              <Button
                type="button"
                size="lg"
                className="h-12 w-full text-base tracking-widest"
                disabled={!derived.isValid}
                aria-label="Confirm Team"
                onClick={handleConfirm}
              >
                <Check className="mr-2.5 size-5" aria-hidden="true" />
                Confirm Team
              </Button>
              {!derived.isValid && (
                <p
                  className="text-center text-xs"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  {!derived.hasTeamName
                    ? "Enter a team name"
                    : !derived.hasEnoughPlayers
                      ? `Need ${11 - state.players.length} more player(s)`
                      : derived.overBudget
                        ? "Over budget"
                        : "Check player names"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
